// [EFFECTIVE-MONTH 2026-07-01] The app-wide "effective current month" (YYYY-MM), client side.
//
// The month does NOT roll over at midnight on the 1st — it rolls over at 12:30 PM IST
// on the 1st. Until then the previous month is still "current" (so month-end editing,
// access, month-dropdown default and labels all belong to it during 00:00–12:30 IST on
// the 1st). Evaluated in IST (Asia/Kolkata) so it's correct regardless of the viewer's
// timezone, mirroring the server's services/effectiveMonth.ts so client gating/labels
// match the server's rules exactly. Single source of truth on the client — both
// InventoryPage.getCurrentMonth and the idle prefetch delegate here, so their month
// (and therefore the SWR cache keys) always line up.

/* [MONTH-COST 2026-09-23] The formatter is built ONCE. Constructing an Intl.DateTimeFormat with
   options costs ~100us, and this function is called per ROW (the status-colour helpers) and inside
   the row filter, so Inventory ran it thousands of times per table build: profiled on 22 Sep it was
   1,161 ms of the freeze that follows a tab switch. Same trap, same fix as the formatMoney sweep
   (see [[tab-switch-slowness-root-cause]] in memory): hoist the Intl.*Format, never build one in a
   hot path. */
const IST_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

/* And the answer itself is remembered for the wall-clock minute it was computed in. The value can
   only change at a minute boundary (the roll-over is 12:30 IST to the minute), and IST is a
   half-hour offset, so its minutes tick with UTC's — within one minute the answer cannot differ.
   Measured: 101us -> 0.08us per call; 8,000 calls go from 807 ms to 1 ms, same string every time. */
let cachedMinute = -1;
let cachedMonth = '';

export function effectiveCurrentMonth(): string {
  const now = Date.now();
  const minuteKey = Math.floor(now / 60_000);
  if (minuteKey === cachedMinute) return cachedMonth;

  const parts = IST_PARTS.formatToParts(new Date(now));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  let y = get('year');
  let m = get('month');
  const day = get('day');
  const hour = get('hour');
  const minute = get('minute');
  // On day 1 before 12:30 IST the previous month is still current.
  if (day === 1 && (hour < 12 || (hour === 12 && minute < 30))) {
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
  }
  cachedMinute = minuteKey;
  cachedMonth = `${y}-${String(m).padStart(2, '0')}`;
  return cachedMonth;
}

/** Add `k` months to a YYYY-MM string, returning YYYY-MM. */
export function addMonthsToYYYYMM(ym: string, k: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + k, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// [MONTH-FLOOR 2026-07-10] The dashboard's usable history starts in January 2026, so
// month dropdowns never offer an earlier month. Mirrors the server's MIN_DROPDOWN_MONTH
// (server/services/effectiveMonth.ts). Most dropdowns are floored server-side; a
// client-GENERATED list (e.g. the EOM month picker) filters against this directly.
export const MIN_DROPDOWN_MONTH = '2026-01';

/** True when a 'YYYY-MM' (or longer) month string is at/after the Jan-2026 floor. */
export function atOrAfterMinMonth(m: string): boolean {
  return typeof m === 'string' && m.slice(0, 7) >= MIN_DROPDOWN_MONTH;
}
